import {
  Assignment,
  CheckBoxRounded,
  Close,
  EmailOutlined,
  Preview,
  Restore,
  Save,
  Send
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  Fade,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Typography
} from '@mui/material';
import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Card, Link, Loader, Modal, SMBSelect } from '../../../components';
import { UserContext } from '../../../context';
import {
  createPeopleAgent,
  createPeopleAgentUpload,
  createPeopleAttorney,
  createPeopleClient,
  createPeopleCustoms,
  createPeopleLender,
  createPeopleLenderProcessor,
  createPeopleLenderUpload,
  createPeopleSupplier,
  createPeopleVendor,
  getPeopleTypes,
  impersonateUser,
  resetPerson,
  resetTwoFactorAuth,
  sendClientWelcomeEmail,
  updatePeopleAgent,
  updatePeopleAttorney,
  updatePeopleClient,
  updatePeopleCustoms,
  updatePeopleLender,
  updatePeopleLenderProcessor,
  updatePeopleSupplier,
  updatePeopleVendor,
  migrateUserToEntra
} from '../../../fetch';
import { alphaSort, formatInputPhoneNumber, urlRegex } from '../../../helpers';
import {
  ExternalUserRoles,
  IDropdownResponse,
  IPeopleDetail,
  IVendorDetail
} from '../../../models';
import { AgentFields } from './agent-fields';
import { AttorneyFields } from './attorney-fields';
import { ClientFields } from './client-fields';
import { CustomsFields } from './customs-fields';
import { LenderFields } from './lender-fields';
import { LenderProcessorFields } from './lender-processor-fields';
import { SupplierFields } from './supplier-fields';
import { VendorAffiliateFields } from './vendor-affiliate-fields';
import { useFlags } from '../../../hooks';
import { useQueryClient } from 'react-query';

interface IAddEditExternalUserModal {
  open: boolean;
  onClose: (hasSubmitted?: boolean) => void;
  currentExternalUser?: IPeopleDetail;
  fetchExternalUsers?: () => {};
  currentVendor?: IVendorDetail;
  addUserToVendor?: (val: number, roles: string[], shouldReload?: boolean) => void;
  initialValues?: any;
  shouldDisablePersonType?: boolean;
  modalTitle?: string;
  isVendorAffiliate?: boolean;
  handleExternalEdit?: (id: string, showLoading?: boolean) => void;
}

const AddEditExternalUserSchema = Yup.object().shape({
  // common fields
  firstName: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.Supplier,
    then: Yup.string().notRequired(),
    otherwise: Yup.string().required('Required').max(255, 'Max 255 characters')
  }),
  lastName: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.Supplier,
    then: Yup.string().notRequired(),
    otherwise: Yup.string().required('Required').max(255, 'Max 255 characters')
  }),
  email: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.Supplier,
    then: Yup.string().notRequired(),
    otherwise: Yup.string().email().required('Required')
  }),
  phoneNumber: Yup.mixed().when('personTypeId', {
    is: val =>
      val === ExternalUserRoles.VendorAffiliate ||
      val === ExternalUserRoles.Purchaser ||
      val === ExternalUserRoles.InternalAgent,
    then: Yup.string().max(255, 'Max 255 characters').required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  personTypeId: Yup.string(),
  partners: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.VendorAffiliate,
    then: Yup.array().of(
      Yup.object()
        .shape({
          partnerId: Yup.string().required('Required'),
          roles: Yup.array()
            .of(Yup.string().required('Required'))
            .min(1, 'Required')
            .required('Required')
        })
        .required('Required')
    ),
    otherwise: Yup.array().notRequired()
  }),
  isActive: Yup.boolean(),
  // attorneys
  attorneyOfficeId: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.Attorney,
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  // agents
  isSalesAdmin: Yup.boolean(),
  agency: Yup.string(),
  cellPhoneNumber: Yup.string().max(255, 'Max 255 characters'),
  webSite: Yup.string().matches(urlRegex, 'URL is not valid'),
  includeAgentNameInUrl: Yup.boolean(),
  agentNeighborhoodIds: Yup.array(),
  // clients
  agentId: Yup.string()
    .nullable()
    .when('personTypeId', {
      is: ExternalUserRoles.Purchaser,
      then: Yup.string().required('Required')
    }),

  address: Yup.string(),
  address2: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zip: Yup.string(),
  contractName: Yup.string(),
  buyerHasAgent: Yup.boolean(),
  agentFirstName: Yup.string(),
  agentLastName: Yup.string(),
  agentEmail: Yup.string(),
  agentPhone: Yup.string(),
  // suppliers specifc
  workplace: Yup.mixed().when('personTypeId', {
    is: val =>
      val === ExternalUserRoles.Custom ||
      val === ExternalUserRoles.Supplier ||
      val === ExternalUserRoles.Lender ||
      val === ExternalUserRoles.InternalAgent,
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  sageVendorId: Yup.string(),
  additionalContactEmails: Yup.string(),
  fax: Yup.string(),
  supplierAcceptsBidRequests: Yup.boolean(),
  // lenders
  lenderStatus: Yup.mixed().when('personTypeId', {
    is: (val: string) => val === ExternalUserRoles.Lender,
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  uploadPersonalFile: Yup.mixed(),
  uploadLogoFile: Yup.mixed(),
  lenderTerritoryIds: Yup.array(),
  // lender processor
  assignedLenders: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.LenderProcessor,
    then: Yup.array().min(1, 'Atleast 1 Lender is required').required('Required'),
    otherwise: Yup.array().notRequired()
  }),
  // employees
  adDisplayName: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.Employee,
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  adUserName: Yup.mixed().when('personTypeId', {
    is: val => val === ExternalUserRoles.Employee,
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  bio: Yup.string(),
  // customs specific
  roles: Yup.array()
});

export const AddEditExternalUser: FC<IAddEditExternalUserModal> = ({
  open,
  onClose,
  currentExternalUser,
  fetchExternalUsers,
  currentVendor,
  addUserToVendor,
  initialValues,
  shouldDisablePersonType,
  modalTitle,
  isVendorAffiliate,
  handleExternalEdit
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [isLoadingPartnerTypes, setIsLoadingPartnerTypes] = useState(false);
  const [partnerTypes, setPartnerTypes] = useState<IDropdownResponse[] | null>(null);
  const [isImpersonating, setImpersonating] = useState<boolean>(false);
  const [isResettingUser, setIsResettingUser] = useState<boolean>(false);
  const [isResettingAuth, setResettingAuth] = useState(false);
  const [isSendingWelcomeEmail, setIsSendingWelcomeEmail] = useState<boolean>(false);
  // Set input fields to be disabled on initial load to prevent password managers from filling them out.
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true);

  const { convertToEntraButton } = useFlags();

  const { loadUser } = useContext(UserContext);

  const handleResetAuth = async (original: any, showLoader?: boolean) => {
    const result = window.confirm('Are you sure you want to reset two factor auth for this user?');
    if (result) {
      try {
        await resetTwoFactorAuth(original.email);
        enqueueSnackbar('Two factor auth reset!', {
          variant: 'success'
        });
      } catch (error: any) {
        const variant =
          error?.Detail &&
          (error.Detail.includes('User not enrolled in MFA') ||
            error.Detail.includes('All user MFA enrollments inactive'))
            ? 'warning'
            : 'error';
        enqueueSnackbar(error?.Detail ?? 'User does not exist', {
          variant: variant
        });
      }
    }
  };

  const fetchPartnerTypes = async () => {
    try {
      setIsLoadingPartnerTypes(true);
      const res = await getPeopleTypes();
      setPartnerTypes(
        alphaSort(
          res.filter(val => val.value !== 'Employee'),
          'description'
        )
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingPartnerTypes(false);
    }
  };

  // Disable input fields on load to prevent password managers from auto-filling
  useEffect(() => {
    if (!open) {
      setIsFormDisabled(true);
      return;
    }

    const timer = setTimeout(() => {
      setIsFormDisabled(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [open]);

  useEffect(() => {
    if (!isVendorAffiliate) {
      fetchPartnerTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendorAffiliate]);

  const getPartners = () => {
    if (currentExternalUser?.partners) {
      return currentExternalUser?.partners?.map(part => ({
        partnerId: part.partnerId,
        roles: part.roles
      }));
    }
    if (currentVendor) {
      return [{ partnerId: currentVendor.vendorId, roles: [] }];
    }
    return [];
  };

  const showImpersonateButton = useMemo(
    () =>
      currentExternalUser &&
      currentExternalUser?.personTypeId !== 'Supplier' &&
      currentExternalUser?.personTypeId !== 'Employee',
    [currentExternalUser]
  );

  function renderRegenerateEmailButton() {
    if (!currentExternalUser?.canRegenerateWelcomeEmail) {
      return null;
    }

    return (
      <LoadingButton
        type="button"
        startIcon={<EmailOutlined />}
        disabled={isSendingWelcomeEmail}
        loading={isSendingWelcomeEmail}
        loadingPosition="start"
        color="secondary"
        onClick={async () => {
          try {
            setIsSendingWelcomeEmail(true);
            await sendClientWelcomeEmail(currentExternalUser?.personId);
            enqueueSnackbar('Email Generated', {
              variant: 'success'
            });
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? `Error sending welcome email, please try again.`, {
              variant: 'error'
            });
          } finally {
            setIsSendingWelcomeEmail(false);
          }
        }}
      >
        Regenerate Welcome Email
      </LoadingButton>
    );
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          // common fields
          firstName: currentExternalUser?.firstName ?? '',
          lastName: currentExternalUser?.lastName ?? '',
          email: currentExternalUser?.email ?? '',
          phoneNumber:
            currentExternalUser && currentExternalUser.phoneNumber
              ? formatInputPhoneNumber(currentExternalUser.phoneNumber)
              : '',
          personTypeId: currentExternalUser?.personTypeId
            ? currentExternalUser?.personTypeId
            : currentVendor || isVendorAffiliate
            ? 'VendorAffiliate'
            : '',
          isActive: currentExternalUser?.isActive ?? true,
          // attorneys
          attorneyOfficeId: currentExternalUser?.attorneyOfficeId ?? '',
          partners: getPartners(),
          // agent fields
          cellPhoneNumber:
            currentExternalUser && currentExternalUser.cellPhoneNumber
              ? formatInputPhoneNumber(currentExternalUser.cellPhoneNumber)
              : '',
          isSalesAdmin: currentExternalUser?.isSalesAdmin ?? false,
          isSalesCoordinator: currentExternalUser?.isSalesCoordinator ?? false,
          webSite: currentExternalUser?.webSite ?? '',
          includeAgentNameInUrl: currentExternalUser?.includeAgentNameInUrl ?? false,
          agentNeighborhoodIds: currentExternalUser?.agentNeighborhoodIds ?? [],
          // clients
          agentId: currentExternalUser?.agentId ? currentExternalUser?.agentId : '',
          hideBuilds: currentExternalUser?.hideBuilds ?? false,
          address: currentExternalUser?.address ?? '',
          address2: currentExternalUser?.address2 ?? '',
          city: currentExternalUser?.city ?? '',
          state: currentExternalUser?.state ?? '',
          zip: currentExternalUser?.zip ?? '',
          contractName: currentExternalUser?.contractName ?? '',
          buyerHasAgent: currentExternalUser?.buyerHasAgent ?? false,
          agentFirstName: currentExternalUser?.agentFirstName ?? '',
          agentLastName: currentExternalUser?.agentLastName ?? '',
          agentEmail: currentExternalUser?.agentEmail ?? '',
          agentPhone: currentExternalUser?.agentPhone ?? '',
          // suppliers
          sageVendorId: currentExternalUser?.sageVendorId
            ? `${currentExternalUser?.sageVendorId}`
            : '',
          additionalContactEmails: currentExternalUser?.additionalContactEmails ?? '',
          fax: currentExternalUser?.fax ?? '',
          supplierAcceptsBidRequests: currentExternalUser?.supplierAcceptsBidRequests ?? false,
          // lender specific
          workplace: currentExternalUser?.workplace ?? '',
          lenderStatus: currentExternalUser?.lenderStatus ?? 'NotApproved',
          lenderTerritoryIds: currentExternalUser?.lenderTerritoryIds ?? [],
          // lender processor specific
          assignedLenders: currentExternalUser?.assignedLenders ?? '',
          // employee specific
          adUserName: currentExternalUser?.adUsername ?? '',
          adDisplayName: currentExternalUser?.adDisplayName ?? '',
          bio: currentExternalUser?.bio ?? '',
          // customs specific
          roles: currentExternalUser?.roles ?? [],
          uploadPersonalFile: '',
          uploadLogoFile: '',
          ...(initialValues || {})
        }}
        validationSchema={AddEditExternalUserSchema}
        onSubmit={async (values, actions) => {
          const commonFields = {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNumber: values.phoneNumber,
            isActive: values.isActive
          };
          const addressFields = {
            address: values?.address,
            address2: values?.address2,
            city: values?.city,
            state: values?.state,
            zip: values?.zip
          };
          const vendorData = {
            ...commonFields,
            partners: values?.partners ?? []
          };
          const agentData = {
            ...commonFields,
            cellPhoneNumber: values.cellPhoneNumber,
            isSalesAdmin: values.isSalesAdmin,
            isSalesCoordinator: values.isSalesCoordinator,
            workplace: values.workplace,
            webSite: values.webSite,
            includeAgentNameInUrl: values.includeAgentNameInUrl,
            agentNeighborhoodIds: values.agentNeighborhoodIds
          };
          const clientData = {
            ...commonFields,
            ...addressFields,
            agentId: values?.agentId ? Number(values?.agentId) : null,
            contractName: values?.contractName,
            buyerHasAgent: values?.buyerHasAgent,
            agentFirstName: values?.agentFirstName,
            agentLastName: values?.agentLastName,
            agentEmail: values?.agentEmail,
            agentPhone: values?.agentPhone,
            additionalContacts: [],
            hideBuilds: currentExternalUser?.hideBuilds
          };
          const supplierData = {
            ...commonFields,
            ...addressFields,
            cellPhoneNumber: values?.cellPhoneNumber,
            fax: values?.fax,
            workplace: values?.workplace,
            sageVendorId: values?.sageVendorId ? Number(values?.sageVendorId) : null,
            additionalContactEmails: values?.additionalContactEmails,
            supplierAcceptsBidRequests: values?.supplierAcceptsBidRequests
          };
          const lendersData = {
            ...commonFields,
            ...addressFields,
            fax: values?.fax,
            cellPhoneNumber: values?.cellPhoneNumber,
            workplace: values?.workplace,
            lenderStatus: values?.lenderStatus,
            lenderTerritoryIds: values?.lenderTerritoryIds
          };

          const lenderProcessorsData = {
            ...commonFields,
            ...addressFields,
            fax: values?.fax,
            cellPhoneNumber: values?.cellPhoneNumber,
            workplace: values?.workplace,
            lenderStatus: values?.lenderStatus,
            lenderTerritoryIds: values?.lenderTerritoryIds,
            assignedLenders: values?.assignedLenders ? values?.assignedLenders : []
          };

          const attorneysData = {
            ...commonFields,
            ...addressFields,
            attorneyOfficeId: values?.attorneyOfficeId ? Number(values?.attorneyOfficeId) : null
          };

          const customsData = {
            ...commonFields,
            workplace: values?.workplace,
            roles: values?.roles
          };

          if (currentExternalUser) {
            try {
              if (values.personTypeId === 'Custom') {
                await updatePeopleCustoms(currentExternalUser.personId, customsData);
              } else if (values.personTypeId === 'Attorney') {
                await updatePeopleAttorney(currentExternalUser.personId, attorneysData);
              } else if (values.personTypeId === 'LenderProcessor') {
                if (values.uploadPersonalFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadPersonalFile);
                  await createPeopleLenderUpload(
                    currentExternalUser.personId,
                    'personal',
                    formData
                  );
                }

                if (values.uploadLogoFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadLogoFile);
                  await createPeopleLenderUpload(currentExternalUser.personId, 'logo', formData);
                }

                await updatePeopleLenderProcessor(
                  currentExternalUser.personId,
                  lenderProcessorsData
                );
              } else if (values.personTypeId === 'Lender') {
                if (values.uploadPersonalFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadPersonalFile);
                  await createPeopleLenderUpload(
                    currentExternalUser.personId,
                    'personal',
                    formData
                  );
                }

                if (values.uploadLogoFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadLogoFile);
                  await createPeopleLenderUpload(currentExternalUser.personId, 'logo', formData);
                }

                await updatePeopleLender(currentExternalUser.personId, lendersData);
              } else if (values.personTypeId === ExternalUserRoles.Supplier) {
                await updatePeopleSupplier(currentExternalUser.personId, supplierData);
              } else if (values.personTypeId === ExternalUserRoles.Purchaser) {
                await updatePeopleClient(currentExternalUser.personId, clientData);
              } else if (values.personTypeId === ExternalUserRoles.InternalAgent) {
                if (values.uploadPersonalFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadPersonalFile);
                  await createPeopleAgentUpload(currentExternalUser.personId, 'personal', formData);
                }

                if (values.uploadLogoFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadLogoFile);
                  await createPeopleAgentUpload(currentExternalUser.personId, 'logo', formData);
                }

                await updatePeopleAgent(currentExternalUser.personId, agentData);
              } else {
                await updatePeopleVendor(currentExternalUser.personId, vendorData);
              }
              enqueueSnackbar('User Updated!', {
                variant: 'success'
              });
              onClose(true);
              fetchExternalUsers && fetchExternalUsers();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'User was not updated', {
                variant: 'error'
              });
            }
          } else {
            try {
              let newPersonId = null;

              if (values.personTypeId === ExternalUserRoles.Custom) {
                newPersonId = await createPeopleCustoms(customsData);
              } else if (values.personTypeId === ExternalUserRoles.Attorney) {
                newPersonId = await createPeopleAttorney(attorneysData);
              } else if (values.personTypeId === ExternalUserRoles.LenderProcessor) {
                newPersonId = await createPeopleLenderProcessor(lenderProcessorsData);
                if (values.uploadPersonalFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadPersonalFile);
                  await createPeopleLenderUpload(newPersonId, 'personal', formData);
                }

                if (values.uploadLogoFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadLogoFile);
                  await createPeopleLenderUpload(newPersonId, 'logo', formData);
                }
              } else if (values.personTypeId === ExternalUserRoles.Lender) {
                newPersonId = await createPeopleLender(lendersData);
                if (values.uploadPersonalFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadPersonalFile);
                  await createPeopleLenderUpload(newPersonId, 'personal', formData);
                }

                if (values.uploadLogoFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadLogoFile);
                  await createPeopleLenderUpload(newPersonId, 'logo', formData);
                }
              } else if (values.personTypeId === ExternalUserRoles.Supplier) {
                newPersonId = await createPeopleSupplier(supplierData);
              } else if (values.personTypeId === ExternalUserRoles.Purchaser) {
                newPersonId = await createPeopleClient(clientData);
              } else if (values.personTypeId === ExternalUserRoles.InternalAgent) {
                newPersonId = await createPeopleAgent(agentData);
                if (values.uploadPersonalFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadPersonalFile);
                  await createPeopleAgentUpload(newPersonId, 'personal', formData);
                }

                if (values.uploadLogoFile) {
                  const formData = new FormData();
                  formData.append('Image', values.uploadLogoFile);
                  await createPeopleAgentUpload(newPersonId, 'logo', formData);
                }
              } else {
                newPersonId = await createPeopleVendor(vendorData);
              }

              if (addUserToVendor) {
                // Always the first when adding from vendor page
                addUserToVendor(newPersonId, values.partners[0].roles, false);
              } else {
                enqueueSnackbar('User Created!', {
                  variant: 'success'
                });
              }

              onClose(true);
              fetchExternalUsers && fetchExternalUsers();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'User was not created', {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur,
          setValues
        }) => {
          const resetAllFields = (personTypeId: string) => {
            // reset fields whenever the person type changes
            setValues({
              firstName: '',
              lastName: '',
              email: '',
              phoneNumber: '',
              personTypeId,
              isActive: true,
              // attorneys
              attorneyOfficeId: '',
              partners:
                personTypeId === 'VendorAffiliate'
                  ? [
                      {
                        roles: [] as any,
                        partnerId: '' as any
                      }
                    ]
                  : [],
              // agent fields
              cellPhoneNumber: '',
              isSalesAdmin: false,
              isSalesCoordinator: false,
              webSite: '',
              includeAgentNameInUrl: false,
              agentNeighborhoodIds: [],
              // clients
              agentId: '',
              address: '',
              address2: '',
              city: '',
              state: '',
              zip: '',
              contractName: '',
              buyerHasAgent: false,
              agentFirstName: '',
              agentLastName: '',
              agentEmail: '',
              agentPhone: '',
              // suppliers
              sageVendorId: '',
              additionalContactEmails: '',
              fax: '',
              supplierAcceptsBidRequests: false,
              //lender processors
              lendorPersonId: '',
              // lender specifc
              workplace: '',
              lenderStatus: personTypeId === 'Lender' ? 'NotApproved' : '',
              uploadLogoFile: null,
              uploadPersonalFile: null,
              // employee specific
              adUserName: '',
              adDisplayName: '',
              bio: '',
              // customs specific
              roles: []
            });
          };
          const handleClose = () => {
            if (!deepEqual(initialValues, values)) {
              const result = window.confirm(
                'You have unsaved changes, are you sure you want to exit?'
              );
              if (result) {
                resetForm();
                onClose();
              } else {
                return;
              }
            } else {
              onClose();
            }
          };
          return (
            <Modal
              open={open}
              onClose={() => {
                handleClose();
              }}
              maxWidth="md"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              {isImpersonating && <Loader type="fullscreen" position="centered" />}
              <Fade in={open}>
                <Box mt={1}>
                  <Form onSubmit={handleSubmit} autoComplete="none">
                    <Box marginBottom="0.5rem" display="flex" flexWrap="wrap">
                      <Typography sx={{ flex: 1 }} variant="h5">
                        {modalTitle ||
                          (currentExternalUser ? `Edit External User` : 'Add External User')}
                      </Typography>
                      <Box display="flex" alignItems="center" flexWrap="wrap">
                        {showImpersonateButton && (
                          <LoadingButton
                            startIcon={<Preview />}
                            loading={isImpersonating}
                            loadingPosition="start"
                            disabled={isImpersonating}
                            color="secondary"
                            onClick={async () => {
                              try {
                                setImpersonating(true);
                                await impersonateUser(currentExternalUser.personId);
                                loadUser();
                                queryClient.invalidateQueries(['getAvailableNavigation']);
                                enqueueSnackbar('Impersonation Successful! Redirecting...', {
                                  variant: 'success'
                                });
                                // hack here to prevent the user from ever being able to go "back" in browser history
                                // so we push into the browser history intially
                                if (currentExternalUser?.personTypeId === 'VendorAffiliate') {
                                  history.push('/vendors');
                                } else {
                                  history.push('/');
                                }
                                return setTimeout(() => {
                                  for (let i = 0; i < 50; i++) {
                                    if (currentExternalUser?.personTypeId === 'VendorAffiliate') {
                                      history.push('/vendors');
                                    } else {
                                      history.push('/');
                                    }
                                  }
                                  setImpersonating(false);
                                }, 2000);
                              } catch (error) {
                                return enqueueSnackbar(
                                  error?.Detail ?? 'Error impersonating user.',
                                  {
                                    variant: 'error'
                                  }
                                );
                              }
                            }}
                          >
                            Impersonate
                          </LoadingButton>
                        )}

                        {renderRegenerateEmailButton()}

                        {!!currentExternalUser && (
                          <LoadingButton
                            type="button"
                            startIcon={<Restore />}
                            disabled={isResettingUser}
                            loading={isResettingUser}
                            loadingPosition="start"
                            color="secondary"
                            onClick={async () => {
                              const result = window.confirm(
                                `Are you sure you want to reset this password?`
                              );
                              if (result) {
                                try {
                                  setIsResettingUser(true);
                                  await resetPerson(currentExternalUser?.personId);
                                  enqueueSnackbar('User emailed reset password instructions!', {
                                    variant: 'success'
                                  });
                                } catch (error) {
                                  enqueueSnackbar(
                                    error?.Detail ??
                                      `Error resetting user password, please try again.`,
                                    {
                                      variant: 'error'
                                    }
                                  );
                                } finally {
                                  setIsResettingUser(false);
                                }
                              }
                            }}
                          >
                            Reset Password
                          </LoadingButton>
                        )}
                        {!!currentExternalUser && (
                          <LoadingButton
                            type="button"
                            startIcon={<Restore />}
                            loading={isResettingAuth}
                            loadingPosition="start"
                            color="secondary"
                            onClick={async () => {
                              setResettingAuth(true);
                              await handleResetAuth(currentExternalUser);
                              setResettingAuth(false);
                            }}
                          >
                            Reset Two Factor Auth
                          </LoadingButton>
                        )}
                      </Box>
                    </Box>
                    <Divider />
                    {!currentVendor && (
                      <Box mt={1} mb={1}>
                        <Grid container spacing={1} alignItems="center">
                          {currentExternalUser && !currentExternalUser?.isActive && (
                            // Only display toggle when user is inactive, used for reactivating user
                            <Grid item xs={12} sm={'auto'}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    color="primary"
                                    checked={values.isActive}
                                    id="active-inactive"
                                    disabled={isFormDisabled}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                      checked: boolean
                                    ) => {
                                      setFieldValue('isActive', checked);
                                    }}
                                  />
                                }
                                label="Active"
                              />
                            </Grid>
                          )}
                          {!isVendorAffiliate && (
                            <Grid item flex={{ xs: '0 0 100%', sm: '1' }} xs={12} sm={'auto'}>
                              <SMBSelect
                                variant="outlined"
                                handleClear={() => {
                                  resetAllFields('');
                                }}
                                onChange={e => {
                                  resetAllFields(e.target.value);
                                }}
                                disabled={
                                  isLoadingPartnerTypes ||
                                  isFormDisabled ||
                                  !!currentExternalUser ||
                                  shouldDisablePersonType
                                }
                                name={`personTypeId`}
                                id={`personTypeId`}
                                label="Person Type"
                                size="small"
                                value={values.personTypeId}
                                placeholder="Select Person Type"
                              >
                                {partnerTypes &&
                                  partnerTypes.map((type: IDropdownResponse, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={type.value}>
                                        {type.description}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                    {values.personTypeId && (
                      <Card title="Info" icon={<Assignment />}>
                        <Grid container spacing={1}>
                          {values.personTypeId === ExternalUserRoles.Supplier && (
                            <SupplierFields
                              values={values}
                              isFormDisabled={isFormDisabled}
                              handleBlur={handleBlur}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                            />
                          )}

                          {values.personTypeId === ExternalUserRoles.Lender && (
                            <LenderFields
                              values={values}
                              isFormDisabled={isFormDisabled}
                              handleBlur={handleBlur}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                              currentExternalPersonId={currentExternalUser?.personId}
                            />
                          )}
                          {values.personTypeId === ExternalUserRoles.LenderProcessor && (
                            <LenderProcessorFields
                              values={values}
                              isFormDisabled={isFormDisabled}
                              handleBlur={handleBlur}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                              currentExternalPersonId={currentExternalUser?.personId}
                              initialValues={initialValues}
                            />
                          )}
                          {values.personTypeId === ExternalUserRoles.Custom && (
                            <CustomsFields
                              values={values}
                              isFormDisabled={isFormDisabled}
                              handleBlur={handleBlur}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                            />
                          )}
                          {values.personTypeId === ExternalUserRoles.Attorney && (
                            <AttorneyFields
                              values={values}
                              isFormDisabled={isFormDisabled}
                              handleBlur={handleBlur}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                            />
                          )}
                          {values.personTypeId === ExternalUserRoles.VendorAffiliate && (
                            <VendorAffiliateFields
                              values={values}
                              isFormDisabled={isFormDisabled}
                              handleBlur={handleBlur}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                            />
                          )}
                          {values.personTypeId === ExternalUserRoles.InternalAgent && (
                            <AgentFields
                              values={values}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              isFormDisabled={isFormDisabled}
                              currentExternalPersonId={currentExternalUser?.personId}
                            />
                          )}
                          {values.personTypeId === ExternalUserRoles.Purchaser && (
                            <ClientFields
                              values={values}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              isFormDisabled={isFormDisabled}
                              currentExternalPersonId={currentExternalUser?.personId}
                            />
                          )}
                        </Grid>
                      </Card>
                    )}

                    <Box marginTop="1rem" display="flex" alignItems="center">
                      <Box flex={1}>
                        <Button
                          disabled={!dirty || isSubmitting || !isValid}
                          type="submit"
                          startIcon={<Save />}
                          variant="contained"
                          color="primary"
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="contained"
                          color="inherit"
                          startIcon={<Close />}
                          onClick={() => {
                            handleClose();
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                      {currentExternalUser && convertToEntraButton && (
                        <Box sx={{ textAlign: 'right' }}>
                          <LoadingButton
                            type="button"
                            variant="contained"
                            color="secondary"
                            disabled={isSendingWelcomeEmail || !!currentExternalUser?.userId}
                            loading={isSendingWelcomeEmail}
                            loadingPosition="start"
                            startIcon={
                              !!currentExternalUser?.userId ? <CheckBoxRounded /> : <Send />
                            }
                            onClick={async () => {
                              const result = window.confirm(
                                'Are you sure you want to invite them to Entra?'
                              );
                              if (result) {
                                try {
                                  setIsSendingWelcomeEmail(true);
                                  await migrateUserToEntra(currentExternalUser?.personId);
                                  handleExternalEdit(`${currentExternalUser?.personId}`, false);
                                  enqueueSnackbar('Invitation sent!', {
                                    variant: 'success'
                                  });
                                } catch (error) {
                                  enqueueSnackbar(error?.Detail ?? 'Error sending invitation', {
                                    variant: 'error'
                                  });
                                } finally {
                                  setIsSendingWelcomeEmail(false);
                                }
                              }
                            }}
                          >
                            {!!currentExternalUser?.userId
                              ? 'Entra Invitation Sent'
                              : 'Invite to Entra'}
                          </LoadingButton>
                          {!!currentExternalUser?.userId && (
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              User exists in the Entra environment already, click{' '}
                              <Link
                                to={`/admin/user-tools/unified-user-management/${currentExternalUser.userId}`}
                              >
                                here
                              </Link>{' '}
                              to view in User Management.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Form>
                </Box>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
